import { Flex, VStack } from '@chakra-ui/react';
import {
  ListingComparableSetFragment,
  ListingFragment,
  MlsListingObject,
  PropertyDetailsFragment,
  PublicListingFragment,
} from '@client/graphql/__generated__/types';
import { useMemo } from 'react';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { ComparableState } from '../../../pages/ListingSetup/Comparables/ComparablesPage';
import { ComparableColumn } from '../../Listings/Comparables/ComparableColumns/ComparableColumn';
import { DraggableComparableColumns } from '../../Listings/Comparables/DraggableComparableColumns';
import {
  getPropertyDetails,
  transformPropertyDetailsToMlsListing,
} from '../../Listings/ListingPage/utils';
import { ExploreProvider } from '../../MapSearch/ExploreContext';
import { ComparablesContainer } from '../styledComponents';
import { EditCompsMap } from './EditCompsMap/EditCompsMap';
import { EditCompsMapFilters } from './EditCompsMap/EditCompsMapFilters';
import { EditCompsMapSidebar } from './EditCompsMap/EditCompsMapSidebar/EditCompsMapSidebar';

interface EditCompsProps {
  geocoderInputRef: React.RefObject<HTMLInputElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  comparableSet?: ListingComparableSetFragment;
  comps: ComparableState[];
  listing?: ListingFragment | PublicListingFragment;
  setComps: (comps: ComparableState[]) => void;
  onAdd: () => void;
  initialSelectedComp?: ComparableState;
  onAdjustmentsUpdate?: () => void;
  propertyDetails?: PropertyDetailsFragment;
}
export const EditComps = ({
  geocoderInputRef,
  containerRef,
  comparableSet,
  comps,
  listing,
  propertyDetails,
  setComps,
  onAdd,
  initialSelectedComp,
  onAdjustmentsUpdate,
}: EditCompsProps) => {
  const isMobile = useIsMobile();

  const normalizedPropertyDetails = useMemo(
    () => getPropertyDetails(listing, propertyDetails),
    [listing, propertyDetails]
  );

  // If the listing is off market, we need to transform the property details to an MlsListingFragment structure.
  // This is a hack to allow PropertyDetails to be used with the ComparableColumn components.
  const mlsListing = listing
    ? listing.mlsListing
    : transformPropertyDetailsToMlsListing(propertyDetails);

  return (
    <ExploreProvider showClosedStatus>
      <VStack gap={{ base: 0, md: 4 }} width="100%">
        <Flex
          direction="column"
          height={{ base: '70vh', md: '80vh' }}
          position="relative"
          width="100%"
        >
          <EditCompsMapFilters
            geocoderInputRef={geocoderInputRef}
            listing={listing}
          />
          <EditCompsMap
            comps={comps}
            initialSelectedComp={initialSelectedComp}
            mainListing={normalizedPropertyDetails}
            setComps={setComps}
          />
          {!isMobile && (
            <EditCompsMapSidebar
              comparableSet={comparableSet}
              comps={comps}
              containerRef={containerRef}
              listing={normalizedPropertyDetails}
              setComps={setComps}
              onAdd={onAdd}
            />
          )}
        </Flex>
        {isMobile && (
          <EditCompsMapSidebar
            comparableSet={comparableSet}
            comps={comps}
            containerRef={containerRef}
            listing={normalizedPropertyDetails}
            setComps={setComps}
            onAdd={onAdd}
          />
        )}
        <ComparablesContainer width="100%">
          <ComparableColumn
            isMainListing
            isEditMode={true}
            mainMlsListing={mlsListing as MlsListingObject}
            mlsListing={mlsListing as MlsListingObject}
            showAdjustments={true}
            showNotes={true}
          />
          <DraggableComparableColumns
            comparableSet={comparableSet}
            comparables={comps}
            isEditMode={true}
            mainMlsListing={mlsListing as MlsListingObject}
            setComparables={setComps}
            showAdjustments={true}
            showNotes={true}
            onAdjustmentsUpdate={onAdjustmentsUpdate}
          />
        </ComparablesContainer>
      </VStack>
    </ExploreProvider>
  );
};
